import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * RFIDInput — Componente para captura RFID HID (Tru-Test XRS2i/SRS2i)
 *
 * El lector RFID en modo HID emula un teclado: escribe dígitos muy rápido
 * y termina con Enter. Este componente detecta esa velocidad para distinguir
 * entre escritura humana y escaneo RFID.
 *
 * Props:
 *   onScan(ide: string)  — callback con el IDE escaneado
 *   placeholder           — texto placeholder
 *   disabled              — deshabilitar input
 *   autoFocus             — auto focus on mount
 */
const RFIDInput = ({ onScan, placeholder = 'Escanear RFID o escribir IDE...', disabled = false, autoFocus = true }) => {
  const [buffer, setBuffer] = useState('');
  const [mode, setMode] = useState('idle'); // idle | scanning | manual
  const [lastScan, setLastScan] = useState('');
  const inputRef = useRef(null);
  const keystrokeTimesRef = useRef([]);
  const timeoutRef = useRef(null);

  // Threshold: RFID readers type at < 30ms between keys. Humans > 80ms.
  const RFID_SPEED_THRESHOLD = 50;
  const RFID_MIN_LENGTH = 6;

  const handleKeyDown = useCallback((e) => {
    const now = Date.now();
    const times = keystrokeTimesRef.current;

    if (e.key === 'Enter') {
      e.preventDefault();
      const currentBuffer = buffer + (e.key === 'Enter' ? '' : e.key);

      // Check if this was an RFID scan (fast input)
      if (times.length >= RFID_MIN_LENGTH) {
        const intervals = [];
        for (let i = 1; i < times.length; i++) {
          intervals.push(times[i] - times[i - 1]);
        }
        const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;

        if (avgInterval < RFID_SPEED_THRESHOLD) {
          // RFID scan detected
          setMode('scanning');
          setLastScan(buffer);
          if (onScan) onScan(buffer.trim());
          setBuffer('');
          keystrokeTimesRef.current = [];
          setTimeout(() => setMode('idle'), 2000);
          return;
        }
      }

      // Manual entry (human speed)
      if (buffer.trim().length >= 3) {
        setMode('manual');
        setLastScan(buffer.trim());
        if (onScan) onScan(buffer.trim());
        setBuffer('');
        keystrokeTimesRef.current = [];
        setTimeout(() => setMode('idle'), 2000);
      }
      return;
    }

    // Record keystroke time
    times.push(now);
    if (times.length > 30) times.shift();

    // Clear timeout for resetting buffer
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      keystrokeTimesRef.current = [];
    }, 1000);
  }, [buffer, onScan]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const modeColors = {
    idle: 'border-gray-300 focus:ring-green-500',
    scanning: 'border-green-500 bg-green-50 ring-2 ring-green-400',
    manual: 'border-blue-500 bg-blue-50 ring-2 ring-blue-400',
  };

  const modeLabels = {
    idle: null,
    scanning: 'RFID Detectado',
    manual: 'Entrada Manual',
  };

  return (
    <div className="relative" data-testid="rfid-input-container">
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12h2m4-6v12m4-10v8m4-6v4m4-8v12"/>
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg font-mono text-sm transition-all ${modeColors[mode]}`}
            data-testid="rfid-input"
          />
          {mode !== 'idle' && (
            <div className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold px-2 py-0.5 rounded ${mode === 'scanning' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}>
              {modeLabels[mode]}
            </div>
          )}
        </div>
      </div>
      {lastScan && mode !== 'idle' && (
        <p className="mt-1 text-xs text-green-700" data-testid="rfid-last-scan">
          Ultimo escaneo: <strong className="font-mono">{lastScan}</strong>
        </p>
      )}
      <p className="mt-1 text-xs text-gray-400">
        Tru-Test XRS2i/SRS2i — Modo HID (teclado). Escanee o escriba IDE + Enter.
      </p>
    </div>
  );
};

export default RFIDInput;
